import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, CheckCircle2, HelpCircle, Calendar, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BodyMarker } from "@/types/vision360";

interface Vision360MarkerDetailProps {
  marker: BodyMarker;
  open: boolean;
  onClose: () => void;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case "ativo":
      return { icon: AlertCircle, label: "Ativo", color: "text-amber-500", bg: "bg-amber-50 dark:bg-amber-950/30" };
    case "controlado":
      return { icon: CheckCircle2, label: "Controlado", color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-950/30" };
    case "resolvido":
      return { icon: CheckCircle2, label: "Resolvido", color: "text-green-500", bg: "bg-green-50 dark:bg-green-950/30" };
    case "investigacao":
      return { icon: HelpCircle, label: "Em investigação", color: "text-gray-500", bg: "bg-gray-50 dark:bg-gray-950/30" };
    default:
      return { icon: HelpCircle, label: "Desconhecido", color: "text-gray-400", bg: "bg-gray-50 dark:bg-gray-950/30" };
  }
};

const getSeverityLabel = (severity: number) => {
  const labels: Record<number, { label: string; color: string }> = {
    1: { label: "Leve", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
    2: { label: "Leve-Moderado", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
    3: { label: "Moderado", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
    4: { label: "Grave", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
    5: { label: "Crítico", color: "bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300" },
  };
  return labels[severity] || labels[1];
};

const getCategoryLabel = (category: string) => {
  const labels: Record<string, string> = {
    dor: "Dor",
    lesao: "Lesão",
    cronico: "Crônico",
    metabolico: "Metabólico",
    mental: "Saúde Mental",
    sono: "Sono",
    outro: "Outro",
  };
  return labels[category] || category;
};

const getRegionLabel = (region: string) => {
  const labels: Record<string, string> = {
    cabeca: "Cabeça",
    face: "Face",
    olhos: "Olhos",
    ouvidos: "Ouvidos",
    boca: "Boca",
    pescoco: "Pescoço",
    cervical: "Cervical",
    ombro_direito: "Ombro Direito",
    ombro_esquerdo: "Ombro Esquerdo",
    torax: "Tórax",
    costas_superior: "Costas Superior",
    braco_direito: "Braço Direito",
    braco_esquerdo: "Braço Esquerdo",
    cotovelo_direito: "Cotovelo Direito",
    cotovelo_esquerdo: "Cotovelo Esquerdo",
    antebraco_direito: "Antebraço Direito",
    antebraco_esquerdo: "Antebraço Esquerdo",
    punho_direito: "Punho Direito",
    punho_esquerdo: "Punho Esquerdo",
    mao_direita: "Mão Direita",
    mao_esquerda: "Mão Esquerda",
    toracica: "Torácica",
    abdominal: "Abdominal",
    lombar: "Lombar",
    sacral: "Sacral",
    quadril_direito: "Quadril Direito",
    quadril_esquerdo: "Quadril Esquerdo",
    coxa_direita: "Coxa Direita",
    coxa_esquerda: "Coxa Esquerda",
    joelho_direito: "Joelho Direito",
    joelho_esquerdo: "Joelho Esquerdo",
    perna_direita: "Perna Direita",
    perna_esquerda: "Perna Esquerda",
    tornozelo_direito: "Tornozelo Direito",
    tornozelo_esquerdo: "Tornozelo Esquerdo",
    pe_direito: "Pé Direito",
    pe_esquerdo: "Pé Esquerdo",
    sistemico: "Sistêmico",
    mental: "Mental",
    metabolico: "Metabólico",
    sono: "Sono",
  };
  return labels[region] || region;
};

export function Vision360MarkerDetail({ marker, open, onClose }: Vision360MarkerDetailProps) {
  const statusConfig = getStatusConfig(marker.status);
  const StatusIcon = statusConfig.icon;
  const severityConfig = getSeverityLabel(marker.severity);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {marker.condition}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status e Severidade */}
          <div className="flex flex-wrap gap-2">
            <Badge className={cn("gap-1", statusConfig.bg, statusConfig.color)}>
              <StatusIcon className="h-3 w-3" />
              {statusConfig.label}
            </Badge>
            <Badge className={severityConfig.color}>
              Severidade {marker.severity}/5 - {severityConfig.label}
            </Badge>
          </div>

          {/* Informações */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Região</p>
              <p className="font-medium">{getRegionLabel(marker.region)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Categoria</p>
              <p className="font-medium">{getCategoryLabel(marker.category)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Última Atualização</p>
              <p className="font-medium flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {marker.lastUpdate 
                  ? new Date(marker.lastUpdate).toLocaleDateString("pt-BR")
                  : "N/A"
                }
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Confiança</p>
              <p className="font-medium">{Math.round((marker.confidence || 0) * 100)}%</p>
            </div>
          </div>

          {/* Evidências */}
          {marker.evidences && marker.evidences.length > 0 && (
            <>
              <Separator />
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  Evidências ({marker.evidences.length})
                </p>
                <ul className="space-y-2">
                  {marker.evidences.map((evidence, i) => (
                    <li
                      key={i}
                      className="text-sm p-2 rounded-lg bg-muted/50"
                    >
                      <p className="text-muted-foreground text-xs mb-1">
                        {evidence.sourceType === "smartfile" && "📄 SmartFile"}
                        {evidence.sourceType === "document" && "📋 Documento"}
                        {evidence.sourceType === "note" && "📝 Anotação"}
                        {evidence.date && ` • ${new Date(evidence.date).toLocaleDateString("pt-BR")}`}
                      </p>
                      <p>{evidence.description}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
